import { useCallback, useEffect, useState } from 'react'
import { toast } from '~/components/ui'
import { type Listing, type ParcelFeature } from '~/models'

import { useQuery } from '@tanstack/react-query'
import {
  getParcel,
  getParcels,
  getParcelsByAPNs,
  searchParcels,
} from './parcels.new'

export { getParcel, getParcels, searchParcels }

export function getParcelPath(
  property: Partial<{ country: string; stateAbbr: string; county?: string }>
) {
  return [
    '/US', // property.country
    property.stateAbbr,
    property.county
      ?.replace('County', '')
      .replace('Parish', '')
      .trim()
      .replace(/[^a-z| ]/gi, ''),
  ]
    .filter((v) => v)
    .join('/')
    .toLowerCase()
    .replace(/ /g, '-')
}

// export async function getParcel(parcelApn: string, path: string) {
//   const response = await fetch(
//     `https://app.regrid.com/api/v2/parcels/apn?parcelnumb=${parcelApn}&path=${path}&token=${REGRID_TOKEN}`,
//     {
//       method: 'GET',
//       headers: {
//         'Content-Type': 'application/json',
//       },
//     }
//   )
//   if (response.status === 200) {
//     const data: { parcels: ParcelFeatureCollection } = await response.json()
//     return data.parcels.features[0]
//   } else {
//     throw new Error(
//       `Error fetching parcel: ${response.status} ${response.statusText}`
//     )
//   }
// }

// Temp quick fix for multiple features for single parcel apn
// export async function getParcels(parcelApn: string, path: string) {
//   const response = await fetch(
//     `https://app.regrid.com/api/v2/parcels/apn?parcelnumb=${parcelApn}&path=${path}&token=${REGRID_TOKEN}`,
//     {
//       method: 'GET',
//       headers: {
//         'Content-Type': 'application/json',
//       },
//     }
//   )
//   if (response.status === 200) {
//     const data: { parcels: ParcelFeatureCollection } = await response.json()
//     return data.parcels.features
//   } else {
//     throw new Error(
//       `Error fetching parcel: ${response.status} ${response.statusText}`
//     )
//   }
// }

// export async function searchParcels(
//   latitude: string | number,
//   longitude: string | number
// ) {
//   const response = await fetch(
//     `https://app.regrid.com/api/v2/parcels/point?lon=${longitude}&lat=${latitude}&token=${REGRID_TOKEN}`,
//     {
//       method: 'GET',
//       headers: {
//         'Content-Type': 'application/json',
//       },
//     }
//   )
//   if (response.status === 200) {
//     const data: { parcels: ParcelFeatureCollection } = await response.json()
//     return data.parcels
//   } else {
//     throw new Error(
//       `Error fetching parcels: ${response.status} ${response.statusText}`
//     )
//   }
// }

export function useParcelsSearch({
  latitude,
  longitude,
  showError,
  enabled = true,
}: {
  latitude?: string | number
  longitude?: string | number
  showError?: true
  enabled?: boolean
}) {
  const { data: parcels, isLoading } = useQuery({
    queryKey: ['parcel', 'disable-cache', latitude, longitude],
    queryFn: async () => {
      try {
        const parcels = await searchParcels(latitude!, longitude!, 100)
        return parcels.features
      } catch (error) {
        console.error('Error fetching parcels:', error)
        if (showError) {
          toast({
            variant: 'destructive',
            title: 'Error Fetching Parcel',
            description:
              'There was a problem fetching the parcel data. Please try again or contact support if the problem persists.',
          })
        }
      }
      return []
    },
    enabled: !!latitude && !!longitude && enabled,
  })
  return { parcels, isLoading }
}

export function useGetParcel({
  parcelApn,
  property,
  showError = false,
}: {
  parcelApn?: string
  property: Partial<{ country: string; stateAbbr: string; county?: string }>
  showError?: boolean
}) {
  const [parcel, setParcel] = useState<ParcelFeature | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const _getParcel = useCallback(async () => {
    if (parcelApn) {
      setIsLoading(true)
      try {
        const path = getParcelPath(property)
        const data = await getParcel(parcelApn, path)
        setParcel(data)
      } catch (error) {
        console.error('Error fetching parcel:', error)
        if (showError) {
          toast({
            variant: 'destructive',
            title: 'Error Fetching Parcel',
            description:
              'There was a problem fetching the parcel. Please try again or contact support if the problem persists.',
          })
        }
      }
      setIsLoading(false)
    }
  }, [parcelApn])

  useEffect(() => {
    _getParcel()
  }, [parcelApn])

  return { parcel, isLoading }
}

export function useGetParcels({
  parcelApns,
  enabled,
  property,
}: {
  parcelApns: string[]
  enabled: boolean
  property: Partial<{ country: string; stateAbbr: string; county?: string }>
}) {
  const [parcels, setParcels] = useState<ParcelFeature[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const apns = JSON.stringify(parcelApns)
  const length = parcelApns.length

  const _getParcels = useCallback(
    async (apns: string) => {
      const path = getParcelPath(property)
      const parcelnumbs = JSON.parse(apns)

      if (parcelnumbs.length === 0) return

      setIsLoading(true)

      const parcelData = await getParcelsByAPNs({
        parcelnumbs,
        path,
      })

      setParcels(parcelData.parcels.features)
      setIsLoading(false)
    },
    [parcelApns, property]
  )

  useEffect(() => {
    if (enabled && length > 0) {
      _getParcels(apns)
    }
  }, [enabled, apns, length])

  return { parcels, isLoading }
}

export function useGetListingParcels({
  listings,
  enabled = true,
}: {
  listings: Listing[]
  enabled?: boolean
}) {
  const [parcels, setParcels] = useState<ParcelFeature[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const _getListingParcels = useCallback(async () => {
    const [listing] = listings

    if (!listing) return

    const path = getParcelPath(listing.property)

    const parcelnumbs = listings
      .map((listing) => listing.property.parcelApn)
      .filter((apn) => apn !== undefined)

    if (parcelnumbs.length === 0) return

    setIsLoading(true)

    try {
      const parcelData = await getParcelsByAPNs({
        parcelnumbs,
        path,
      })

      setParcels(parcelData.parcels.features)
    } catch (error) {
      console.error('Error fetching listing parcels:', error)
    } finally {
      setIsLoading(false)
    }
  }, [listings])

  useEffect(() => {
    if (enabled) {
      _getListingParcels()
    }
  }, [enabled])

  return { parcels, isLoading }
}
